import React, { useState } from 'react';

interface CopyLinkButtonProps {
  /** Absolute URL (or relative — we'll promote to absolute) to copy. */
  url: string;
  /** Optional class name for layout-specific styling. */
  className?: string;
  /** Label override — default "Copy link". */
  label?: string;
  /** Render only the icon. The label remains the aria-label + title. */
  iconOnly?: boolean;
}

const abs = (url: string) =>
  url.startsWith('http')
    ? url
    : `${window.location.origin}${url.startsWith('/') ? '' : '/'}${url}`;

/**
 * Clipboard-only sibling to ShareButton. Copies just the URL — no prepended
 * text, no native share sheet. For users who want to paste the link into a
 * browser, a doc, or anywhere that isn't a messenger.
 */
const CopyLinkButton: React.FC<CopyLinkButtonProps> = ({
  url,
  className,
  label = 'Copy link',
  iconOnly = false,
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    const absUrl = abs(url);
    try {
      await navigator.clipboard.writeText(absUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      window.prompt('Copy this link:', absUrl);
    }
  };

  return (
    <button
      type="button"
      className={className || 'copy-link-btn'}
      onClick={handleCopy}
      aria-label={copied ? 'Link copied' : label}
      title={copied ? 'Link copied' : label}
    >
      {copied ? (iconOnly ? '✓' : '✓ Copied') : (iconOnly ? '⧉' : `⧉ ${label}`)}
    </button>
  );
};

export default CopyLinkButton;
